// Timeline de atividades CRM — componente reusável (Story 11.8).
//
// Recebe um ALVO (lead | deal | client) e renderiza as atividades desse alvo
// em ordem decrescente (occurred_at), com:
//  - distinção visual de atividades automáticas (is_system): label "Automático"
//    + fundo levemente diferente, SEM emoji (proibição 07 do DESIGN_SYSTEM).
//  - skeleton no loading, empty/error states, "carregar mais" (cursor).
//
// Projetado pra a Story 11.3 (detalhe do lead) montar:
//   <ActivityTimeline target={{ kind: 'lead', id: lead.id }} canWrite={canWrite} />
// O componente cuida sozinho do fetch (useCrmActivitiesInfinite) — quem consome
// só passa o alvo. Quando `canWrite`, exibe o form inline de nova atividade.

import { useMemo } from 'react';
import {
  CalendarClock,
  Check,
  Mail,
  MessageSquare,
  Phone,
  RefreshCw,
  StickyNote,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { CrmActivityTypeSchema } from '@control-center/shared';
import { formatDayBR, formatBRDateTime } from '@control-center/shared';

import {
  useCrmActivitiesInfinite,
  type CrmActivitiesListFilters,
} from '../../hooks/use-crm-activities';
import { extractErrorMessage } from '../../lib/errors';
import type { CrmActivityEntity } from '../../lib/crm-activities-api';
import { PillButton } from '../clients/PillButton';
import { ActivityForm } from './ActivityForm';

export type ActivityTimelineTarget =
  | { kind: 'lead'; id: string }
  | { kind: 'deal'; id: string }
  | { kind: 'client'; id: string };

interface ActivityTimelineProps {
  target: ActivityTimelineTarget;
  /** Habilita o form inline de nova atividade (admin/comercial — AC8). */
  canWrite?: boolean;
}

const TYPE_META: Record<CrmActivityTypeSchema, { label: string; icon: LucideIcon }> = {
  ligacao: { label: 'Ligação', icon: Phone },
  mensagem: { label: 'Mensagem', icon: MessageSquare },
  reuniao: { label: 'Reunião', icon: Users },
  email: { label: 'E-mail', icon: Mail },
  nota: { label: 'Nota', icon: StickyNote },
  mudanca_estagio: { label: 'Mudança de estágio', icon: RefreshCw },
  proposta_enviada: { label: 'Proposta enviada', icon: Mail },
  tarefa: { label: 'Tarefa', icon: CalendarClock },
};

function targetToFilters(target: ActivityTimelineTarget): CrmActivitiesListFilters {
  switch (target.kind) {
    case 'lead':
      return { lead_id: target.id };
    case 'deal':
      return { deal_id: target.id };
    case 'client':
      return { client_id: target.id };
  }
}

export function ActivityTimeline({ target, canWrite = false }: ActivityTimelineProps): JSX.Element {
  const filters = useMemo(() => targetToFilters(target), [target]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCrmActivitiesInfinite(filters);

  const activities = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);

  return (
    <div className="flex flex-col gap-4">
      {canWrite && <ActivityForm target={target} />}

      {isLoading ? (
        <TimelineSkeleton />
      ) : isError ? (
        <TimelineError
          message={extractErrorMessage(error, 'Não conseguimos carregar a timeline.')}
          onRetry={() => refetch()}
        />
      ) : activities.length === 0 ? (
        <TimelineEmpty />
      ) : (
        <ol className="flex flex-col gap-[2px]">
          {activities.map((activity) => (
            <TimelineItem key={activity.id} activity={activity} />
          ))}
        </ol>
      )}

      {hasNextPage && (
        <div className="flex justify-center">
          <PillButton onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </PillButton>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Item
// =============================================================================

function TimelineItem({ activity }: { activity: CrmActivityEntity }): JSX.Element {
  const meta = TYPE_META[activity.type];
  const Icon = meta.icon;

  // Atividade automática: fundo levemente diferente + label "Automático" (AC7).
  const systemClasses = activity.is_system ? 'border-line bg-panel-2' : 'border-line bg-panel';

  return (
    <li className={`flex gap-3 rounded-[12px] border px-4 py-3 transition-colors ${systemClasses}`}>
      <span
        className="mt-[2px] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line bg-page text-muted"
        aria-hidden
      >
        <Icon size={14} strokeWidth={2} />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] font-semibold uppercase tracking-wider text-muted-3">
            {meta.label}
          </span>
          {activity.is_system && (
            <span className="rounded-[5px] border border-line px-[6px] py-[1px] text-[10px] uppercase tracking-wider text-muted-2">
              Automático
            </span>
          )}
          {activity.type === 'tarefa' && (
            <TaskBadge done={activity.done} dueDate={activity.due_date} />
          )}
        </div>

        <p className="text-[13px] font-medium leading-snug text-ink">{activity.subject}</p>
        {activity.description && (
          <p className="whitespace-pre-line text-[12px] leading-relaxed text-muted">
            {activity.description}
          </p>
        )}

        <div className="mt-[2px] flex flex-wrap items-center gap-x-3 gap-y-[2px] text-[11px] text-muted-2">
          <span>{formatBRDateTime(activity.occurred_at)}</span>
          {activity.author && <span>· {activity.author.name}</span>}
          {activity.done && activity.done_at && (
            <span className="inline-flex items-center gap-1 text-ok">
              <Check size={11} strokeWidth={2.5} />
              Concluída {formatBRDateTime(activity.done_at)}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function TaskBadge({ done, dueDate }: { done: boolean; dueDate: string | null }): JSX.Element {
  if (done) {
    return (
      <span className="rounded-[5px] border border-ok/40 px-[6px] py-[1px] text-[10px] text-ok">
        Concluída
      </span>
    );
  }
  return (
    <span className="rounded-[5px] border border-line px-[6px] py-[1px] text-[10px] text-muted">
      {dueDate ? `Vence ${formatDayBR(dueDate)}` : 'Tarefa'}
    </span>
  );
}

// =============================================================================
// States
// =============================================================================

function TimelineSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-[2px]">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex gap-3 rounded-[12px] border border-line bg-panel px-4 py-3">
          <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-panel-2" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-3 w-24 animate-pulse rounded bg-panel-2" />
            <div className="h-3 w-48 animate-pulse rounded bg-panel-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineEmpty(): JSX.Element {
  return (
    <div className="rounded-[12px] border border-dashed border-line bg-panel py-8 text-center">
      <p className="text-[13px] font-medium text-ink">Sem atividades ainda</p>
      <p className="mx-auto mt-1 max-w-[320px] text-[12px] text-muted">
        Registre ligações, mensagens, reuniões, notas e tarefas pra montar o histórico comercial.
      </p>
    </div>
  );
}

function TimelineError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="rounded-[12px] border border-bad/40 bg-panel p-4">
      <p className="text-[13px] font-medium text-ink">Não conseguimos carregar a timeline.</p>
      <p className="mt-1 text-[12px] text-muted">{message}</p>
      <div className="mt-3">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}
